import React, { useMemo } from 'react';
import { Card, Col, Row } from 'antd';
import { Column, Line } from '@ant-design/charts';
import type { BqKpiDay } from '@/types';
import { dayLabel, fmtNum, fmtPct, fmtUsd } from './bqMath';

const HEIGHT = 220;
const NO_ANIMATION = { animate: false };
const LEGEND = { color: { position: 'top' as const } };

interface Point {
  day: string;
  value: number;
  series: string;
}

/** DAU and new users, revenue (IAP vs ads) and D1/D7 retention by day. */
export const BqTrends: React.FC<{ kpis: BqKpiDay[] }> = ({ kpis }) => {
  const { users, revenue, retention } = useMemo(() => {
    const users: Point[] = [];
    const revenue: Point[] = [];
    const retention: Point[] = [];
    for (const k of kpis) {
      const day = dayLabel(k.date);
      users.push(
        { day, value: k.dau, series: 'DAU' },
        { day, value: k.newUsers, series: 'New users' }
      );
      revenue.push(
        { day, value: k.iapRevenueUsd, series: 'In-app purchases' },
        { day, value: k.adRevenueUsd, series: 'Ads' }
      );
      if (k.d1Retention !== null) retention.push({ day, value: k.d1Retention, series: 'D1' });
      if (k.d7Retention !== null) retention.push({ day, value: k.d7Retention, series: 'D7' });
    }
    return { users, revenue, retention };
  }, [kpis]);

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} lg={8}>
        <Card size="small" title="Players">
          <Line
            data={users}
            xField="day"
            yField="value"
            colorField="series"
            height={HEIGHT}
            legend={LEGEND}
            axis={{ y: { labelFormatter: (v: number) => fmtNum(Number(v)) } }}
            {...NO_ANIMATION}
          />
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card size="small" title="Revenue (USD)">
          <Column
            data={revenue}
            xField="day"
            yField="value"
            colorField="series"
            stack
            height={HEIGHT}
            legend={LEGEND}
            axis={{ y: { labelFormatter: (v: number) => fmtUsd(Number(v)) } }}
            {...NO_ANIMATION}
          />
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card size="small" title="D1 / D7 retention by install day">
          <Line
            data={retention}
            xField="day"
            yField="value"
            colorField="series"
            height={HEIGHT}
            legend={LEGEND}
            axis={{ y: { labelFormatter: (v: number) => fmtPct(Number(v)) } }}
            {...NO_ANIMATION}
          />
        </Card>
      </Col>
    </Row>
  );
};
